import { ReactNode } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { DEFAULT_VALUES, useLocalizedHref } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Image } from "@/components/wpl/image/src";

const backgroundStyles = css`
    background-color: #091e42;
    background-repeat: no-repeat;
    color: #ffffff;
    padding: 32px 0;

    a {
        text-decoration: underline;
    }

    ${UNSAFE_media.only.sm} {
        background-image: linear-gradient(180deg, #b3d4ff 0%, #b3d4ff00 84.73%);
        background-position: center;
        background-size: cover;
        display: flex;
    }

    ${UNSAFE_media.only.md} {
        background-image: url("https://wac-cdn.atlassian.com/dam/jcr:c959cb2a-6aef-4b7b-a737-25ac883e3053/JSW_ProductTour_DarkModeIllustration_Wave.svg?cdnVersion=1203");
        background-position: center top 170px;
        background-size: 1300px;
    }

    ${media.above.lg} {
        background-image: url("https://wac-cdn.atlassian.com/dam/jcr:c959cb2a-6aef-4b7b-a737-25ac883e3053/JSW_ProductTour_DarkModeIllustration_Wave.svg?cdnVersion=1203");
        background-position: center top 50px;
        background-size: 1480px;
    }
`;

const ScalingSection = () => {
    const intl = useIntl();

    return (
        <section css={backgroundStyles}>
            <div
                css={css`
                    padding: 16px;
                    width: 41.66%;
                    max-width: 490px;

                    h2 {
                        font-size: 2.25rem;
                        font-weight: normal;
                        line-height: 1.333;
                        margin-bottom: 24px;
                    }

                    p {
                        font-size: 1rem;
                        line-height: 1.5;
                    }

                    ${media.above.lg} {
                        margin: 0 auto;
                        position: relative;
                        right: 356px;
                    }

                    ${UNSAFE_media.only.md} {
                        margin-left: 9.5%;
                        width: 33%;
                    }

                    ${UNSAFE_media.below.sm} {
                        width: 100%;

                        h2 {
                            font-size: 1.5rem;
                        }
                    }
                `}
            >
                <ContentBlock
                    headerType="h2"
                    headerText={intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.scalingSection.headerText",
                        defaultMessage: "Built for teams of 1 to 35,000"
                    })}
                    bodyText={intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.scalingSection.bodyText",
                        defaultMessage:
                            "A growing team doesn’t need to mean growing pains. With best-of-breed features, security, privacy, and the right tool for every step of your journey - Jira Software allows you to scale without friction - regardless of your company size."
                    })}
                />
            </div>
            <div
                css={css`
                    display: none;
                    background-image: linear-gradient(
                        to bottom,
                        #0065ff,
                        #2684ff
                    );
                    background-position: left 65px center;
                    background-repeat: no-repeat;
                    background-size: 5px 67%;

                    ${UNSAFE_media.below.md} {
                        display: flex;
                    }
                `}
            >
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 42px;
                        padding: 16px;
                    `}
                >
                    <ScalingStepsForSmallScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:b1013bac-01ff-4be2-827a-8a16b76d0a4b/JSW%20free-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.small.jiraFree",
                            defaultMessage: "Jira free"
                        })}
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwareFree"
                                defaultMessage="Jira Software free forever for teams up to 10"
                                values={DEFAULT_VALUES}
                            />
                        }
                    />
                    <ScalingStepsForSmallScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:5cae308d-24a4-40d0-8fe2-ce7f46cd7a02/JSW%20sign-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.small.jiraSoftware",
                            defaultMessage: "Jira Software"
                        })}
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwareStandard"
                                defaultMessage="Jira Software Standard for growing teams"
                                values={DEFAULT_VALUES}
                            />
                        }
                    />
                    <ScalingStepsForSmallScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:c36a7707-da80-46a7-9cc2-38abca992964/Prem%20sign-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwarePremium",
                            defaultMessage: "Jira Software Premium"
                        })}
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwarePremiumScale"
                                defaultMessage="Scale across teams with <a>Jira Software Premium</a>"
                                values={{
                                    a: (str: ReactNode) => (
                                        <a
                                            // eslint-disable-next-line react-hooks/rules-of-hooks
                                            href={useLocalizedHref(
                                                "/software/jira/premium"
                                            )}
                                            data-event-component="linkButton"
                                            data-event-container="textBlock"
                                            data-event="clicked"
                                            data-label="Jira Software Premium"
                                            data-product-key="jiraSoftware"
                                        >
                                            {str}
                                        </a>
                                    ),
                                    ...DEFAULT_VALUES
                                }}
                            />
                        }
                    />
                    <ScalingStepsForSmallScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:e4223ecb-8867-46cb-b73d-4218e4807778/Ent%20sign-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwareEnterprise",
                            defaultMessage: "Jira Software Enterprise"
                        })}
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwareEnterpriseConnect"
                                defaultMessage="Connect and accelerate your entire <a>enterprise</a>"
                                values={{
                                    a: (str: ReactNode) => (
                                        <a
                                            // eslint-disable-next-line react-hooks/rules-of-hooks
                                            href={useLocalizedHref(
                                                "/software/jira/enterprise"
                                            )}
                                            data-event-component="linkButton"
                                            data-event-container="textBlock"
                                            data-event="clicked"
                                            data-label="enterprise"
                                            data-product-key="jiraSoftware"
                                        >
                                            {str}
                                        </a>
                                    ),
                                    ...DEFAULT_VALUES
                                }}
                            />
                        }
                    />
                    <ScalingStepsForSmallScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:c177344d-7e41-4576-b4eb-85b346d6653b/JA%20sign-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.small.jiraAlign",
                            defaultMessage: "Jira Align"
                        })}
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.small.jiraAlignEnterprise"
                                defaultMessage="Enterprise agility with Jira Align"
                                values={DEFAULT_VALUES}
                            />
                        }
                    />
                </div>
            </div>
            <div
                css={css`
                    padding: 16px;
                    margin-top: -80px;
                    margin-bottom: 20px;

                    ${UNSAFE_media.below.md} {
                        display: none;
                    }
                `}
            >
                <div
                    css={css`
                        display: flex;
                        justify-content: center;
                        background-image: linear-gradient(
                            90deg,
                            #2684ff 5.1%,
                            #79f2c0 100%
                        );
                        background-position: center 244px;
                        background-repeat: no-repeat;
                        background-size: 100% 5px;
                        margin: auto;
                        max-width: 1440px;
                    `}
                >
                    <ScalingStepsForLargeScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:b1013bac-01ff-4be2-827a-8a16b76d0a4b/JSW%20free-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.large.jiraFree",
                            defaultMessage: "Jira free"
                        })}
                        cssTop="80px"
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwareFree"
                                defaultMessage="Jira Software free forever for teams up to 10"
                                values={DEFAULT_VALUES}
                            />
                        }
                    />
                    <ScalingStepsForLargeScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:5cae308d-24a4-40d0-8fe2-ce7f46cd7a02/JSW%20sign-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.large.jiraSoftware",
                            defaultMessage: "Jira Software"
                        })}
                        cssTop="60px"
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwareStandard"
                                defaultMessage="Jira Software Standard for growing teams"
                                values={DEFAULT_VALUES}
                            />
                        }
                    />
                    <ScalingStepsForLargeScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:c36a7707-da80-46a7-9cc2-38abca992964/Prem%20sign-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwarePremium",
                            defaultMessage: "Jira Software Premium"
                        })}
                        cssTop="40px"
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwarePremiumScale"
                                defaultMessage="Scale across teams with <a>Jira Software Premium</a>"
                                values={{
                                    a: (str: ReactNode) => (
                                        <a
                                            // eslint-disable-next-line react-hooks/rules-of-hooks
                                            href={useLocalizedHref(
                                                "/software/jira/premium"
                                            )}
                                            data-event-component="linkButton"
                                            data-event-container="textBlock"
                                            data-event="clicked"
                                            data-label="Jira Software Premium"
                                            data-product-key="jiraSoftware"
                                        >
                                            {str}
                                        </a>
                                    ),
                                    ...DEFAULT_VALUES
                                }}
                            />
                        }
                    />
                    <ScalingStepsForLargeScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:e4223ecb-8867-46cb-b73d-4218e4807778/Ent%20sign-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwareEnterprise",
                            defaultMessage: "Jira Software Enterprise"
                        })}
                        cssTop="20px"
                        // disabling eslint because the bodyText prop is a ReactNode
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwareEnterpriseConnect"
                                defaultMessage="Connect and accelerate your entire <a>enterprise</a>"
                                values={{
                                    a: (str: ReactNode) => (
                                        <a
                                            // eslint-disable-next-line react-hooks/rules-of-hooks
                                            href={useLocalizedHref(
                                                "/software/jira/enterprise"
                                            )}
                                            data-event-component="linkButton"
                                            data-event-container="textBlock"
                                            data-event="clicked"
                                            data-label="enterprise"
                                            data-product-key="jiraSoftware"
                                        >
                                            {str}
                                        </a>
                                    ),
                                    ...DEFAULT_VALUES
                                }}
                            />
                        }
                    />
                    <ScalingStepsForLargeScreen
                        imageSrc="https://wac-cdn.atlassian.com/dam/jcr:c177344d-7e41-4576-b4eb-85b346d6653b/JA%20sign-responsive.png?cdnVersion=1203"
                        imageAltText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.scalingSection.large.jiraAlign",
                            defaultMessage: "Jira Align"
                        })}
                        cssTop="0"
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.scalingSection.large.jiraAlignEnterprise"
                                defaultMessage="Enterprise agility with Jira Align"
                                values={DEFAULT_VALUES}
                            />
                        }
                    />
                </div>
            </div>
        </section>
    );
};

interface IconProps {
    imageSrc: string;
    imageAltText: string;
    cssTop?: string;
    bodyText: string | ReactNode;
}

const ScalingStepsForSmallScreen = (props: IconProps) => {
    return (
        <div
            css={css`
                display: flex;
            `}
        >
            <div
                css={css`
                    display: flex;
                    align-items: center;

                    img {
                        height: 100px;
                        min-width: 100px;
                        width: 100px;
                        z-index: 2;
                    }
                `}
            >
                <Image src={props.imageSrc} alt={props.imageAltText} />
            </div>
            <div
                css={css`
                    align-items: center;
                    display: flex;
                    padding-left: 32px;
                    width: 50%;

                    ${UNSAFE_media.below.md} {
                        width: auto;
                    }
                `}
            >
                <p
                    css={css`
                        font-size: 1rem;
                        margin: 0;
                        line-height: 1.5;
                    `}
                >
                    {props.bodyText}
                </p>
            </div>
        </div>
    );
};

const ScalingStepsForLargeScreen = (props: IconProps) => {
    return (
        <div
            css={css`
                align-items: center;
                display: flex;
                flex-direction: column;
                padding: 16px;
                width: 16.66%;
            `}
        >
            <div>
                <div
                    css={css`
                        display: flex;
                        justify-content: center;
                        min-height: 220px;
                        position: relative;

                        img {
                            margin-bottom: 16px;
                            position: relative;
                            top: ${props.cssTop};
                            width: 100px;
                            height: 100px;
                            z-index: 2;
                        }

                        :after {
                            content: "";
                            position: absolute;
                            height: 180px;
                            top: 95px;
                            border: 2px solid #2684ff;
                            background-size: 4px 8px;
                            background-repeat: repeat-y;
                            max-height: -webkit-fill-available;
                            z-index: 1;
                        }
                    `}
                >
                    <Image src={props.imageSrc} alt={props.imageAltText} />
                </div>
                <div
                    css={css`
                        background-color: #2684ff;
                        border-radius: 50%;
                        height: 20px;
                        margin: 0 auto 30px;
                        width: 20px;
                    `}
                />
                <div>
                    <p
                        css={css`
                            font-size: 1rem;
                            margin: 0;
                            text-align: center;
                            line-height: 1.5;
                        `}
                    >
                        {props.bodyText}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ScalingSection;
